import type { GuidedState } from '../../pages/GuidedWalkthroughPage';

interface Props {
  state: GuidedState;
  onChange: (updates: Partial<GuidedState>) => void;
  onNext: () => void;
}

const SECTORS = [
  {
    value: 'nhs' as const,
    icon: '🏥',
    label: 'NHS / Healthcare',
    desc: 'NHS trusts, GP practices, health boards',
  },
  {
    value: 'fca' as const,
    icon: '🏦',
    label: 'Financial Services (FCA-regulated)',
    desc: 'Banks, insurers, wealth managers, FCA-regulated firms',
  },
  {
    value: 'public_sector' as const,
    icon: '🏛️',
    label: 'UK Public Sector',
    desc: 'Government departments, councils, education, emergency services',
  },
  {
    value: 'private' as const,
    icon: '🏢',
    label: 'Private Sector / Other',
    desc: 'Commercial organisations, charities, SMEs',
  },
];

const SIZES = [
  { value: 'small' as const, label: 'Small', desc: 'Up to 250 employees' },
  { value: 'medium' as const, label: 'Medium', desc: '250 to 2,000 employees' },
  { value: 'large' as const, label: 'Large', desc: '2,000+ employees' },
];

function sectorDefaults(sector: GuidedState['sector']): Partial<GuidedState> {
  switch (sector) {
    case 'nhs':
      return { sentinelEnabled: true, retentionDays: 365, archiveYears: 2, needsSecurity: true, needsCompliance: true };
    case 'fca':
      return { sentinelEnabled: true, retentionDays: 730, archiveYears: 5, needsSecurity: true, needsCompliance: true };
    case 'public_sector':
      return { sentinelEnabled: true, retentionDays: 365, archiveYears: 2, needsSecurity: true, needsCompliance: false };
    case 'private':
      return { sentinelEnabled: false, retentionDays: 90, archiveYears: 0, needsSecurity: false, needsCompliance: false };
  }
}

export default function Step1Organisation({ state, onChange, onNext }: Props) {
  const canProceed = !!state.sector && !!state.organisationSize;

  const handleSector = (sector: GuidedState['sector']) => {
    onChange({ sector, ...sectorDefaults(sector) });
  };

  return (
    <div>
      <h2 className="text-[22px] font-bold mb-1" style={{ color: 'var(--text-primary)' }}>Tell us about your organisation</h2>
      <p className="text-[14px] mb-8" style={{ color: 'var(--text-tertiary)' }}>
        We use this to set sensible defaults for compliance and retention requirements.
      </p>

      <div className="mb-8">
        <p className="text-[13px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-secondary)' }}>What sector are you in?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {SECTORS.map((s) => (
            <button
              key={s.value}
              onClick={() => handleSector(s.value)}
              className={[
                'text-left p-4 rounded-lg border-2 transition-all',
                state.sector === s.value
                  ? 'border-[#0078d4] bg-[#0078d4]/15'
                  : 'hover:border-[#0078d4]/50',
              ].join(' ')}
            >
              <div className="flex items-start gap-3">
                <span className="text-[24px] leading-none mt-0.5">{s.icon}</span>
                <div>
                  <p className={[
                    'text-[14px] font-semibold mb-0.5',
                    state.sector === s.value ? 'text-[#0078d4]' : '',
                  ].join(' ')}>{s.label}</p>
                  <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{s.desc}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-8">
        <p className="text-[13px] font-bold uppercase tracking-wide mb-3" style={{ color: 'var(--text-secondary)' }}>How large is your organisation?</p>
        <div className="grid grid-cols-3 gap-3">
          {SIZES.map((s) => (
            <button
              key={s.value}
              onClick={() => onChange({ organisationSize: s.value })}
              className={[
                'text-left p-4 rounded-lg border-2 transition-all',
                state.organisationSize === s.value
                  ? 'border-[#0078d4] bg-[#0078d4]/15'
                  : 'hover:border-[#0078d4]/50',
              ].join(' ')}
            >
              <p className={[
                'text-[14px] font-semibold mb-0.5',
                state.organisationSize === s.value ? 'text-[#0078d4]' : '',
              ].join(' ')}>{s.label}</p>
              <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>{s.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <button
        onClick={onNext}
        disabled={!canProceed}
        className={[
          'w-full py-3 rounded-lg font-semibold text-[14px] transition-all flex items-center justify-center gap-2',
          canProceed
            ? 'bg-[#0078d4] hover:bg-[#106ebe] text-white'
            : 'cursor-not-allowed',
        ].join(' ')}
      >
        Next: Your IT Infrastructure
        <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
      </button>
    </div>
  );
}
